import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { BookSearchResult } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { Cover, authorsOf } from "./BookBits";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.bookSearch;

// Debounced Hardcover search. The parent decides what "pick" means.
/** What the search knows about a result already on the shelf: a different action, a note, or no action at all. */
export interface SearchNote { action?: string; note?: string; disabled?: boolean }

/** Results shown before "Show more"; the rest come from the same answer, no new search. */
const SHOWN = 5;

export function BookSearch({ onPick, picking, disabledIds = [], noteFor }:
  { onPick: (b: BookSearchResult) => void; picking?: number | null; disabledIds?: number[]; noteFor?: (hardcoverId: number) => SearchNote | null }) {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [more, setMore] = useState(false);
  // Search once typing settles (half a second), from three characters; the server keeps answers for a day.
  useEffect(() => {
    const t = setTimeout(() => { setQ(text.trim()); setMore(false); }, 500);
    return () => clearTimeout(t);
  }, [text]);
  const results = useQuery({ queryKey: ["search", q.toLowerCase()], queryFn: () => api.searchBooks(q), enabled: q.length >= 3, staleTime: 30 * 60_000, retry: false });
  const shown = more ? results.data : results.data?.slice(0, SHOWN);

  return (
    <div className="stack-sm">
      <input id="book-search" className="input" type="search" placeholder={txt.searchHardcoverByTitle} value={text}
        onChange={(e) => setText(e.target.value)} autoComplete="off" />
      {results.isError && <p className="error">{describeError(results.error)}</p>}
      {results.isFetching && <p className="muted small">{txt.searching}</p>}
      {results.data && results.data.length === 0 && <p className="muted small">{txt.nothingMatchedTryFewer}</p>}
      {results.data && results.data.length > 0 && (
        <div className="results">
          {shown!.map((b) => {
            const n = noteFor?.(b.hardcoverId) ?? null;
            const taken = disabledIds.includes(b.hardcoverId) || Boolean(n?.disabled);
            return (
              <div key={b.hardcoverId} className={`result${taken ? " taken" : ""}`} role="button" tabIndex={taken ? -1 : 0} aria-disabled={taken || picking === b.hardcoverId}
                onClick={() => { if (!taken && picking !== b.hardcoverId) onPick(b); }}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !taken && picking !== b.hardcoverId) { e.preventDefault(); onPick(b); } }}>
                <Cover book={b} size="sm" />
                <div className="grow">
                  <div className="t">{b.title}</div>
                  <div className="muted small">{authorsOf(b)}{b.releaseYear ? ` · ${b.releaseYear}` : ""}</div>
                  {n?.note && <div className="small" style={{ color: "var(--moss-text)" }}>{n.note}</div>}
                </div>
                <span className={`btn sm${taken || picking === b.hardcoverId ? " ghost" : ""}`} aria-hidden="true">
                  {picking === b.hardcoverId ? txt.adding : n?.action ?? (taken ? txt.onShelf : txt.add)}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {results.data && !more && results.data.length > SHOWN && (
        <button type="button" className="linkish small" style={{ alignSelf: "flex-start" }} onClick={() => setMore(true)}>{txt.showMore(results.data.length - SHOWN)}</button>
      )}
    </div>
  );
}
